/**
 * The three telemetry severity tiers (#4382).
 *
 * Before this module existed the app had exactly ONE tier. `trackSilentProblem` reported
 * to Sentry at level `error` and fired the Mixpanel event `sentry_error`;
 * `trackAbnormalException` / `trackAbnormalError` — used for genuine caught exceptions —
 * did the same, at the same level, under the same event name. So a monitor that fired
 * because the app CAUGHT AND AUTOMATICALLY REPAIRED something was indistinguishable, in
 * both systems, from an unhandled exception that broke the app for a real user. Nobody
 * could build a "real problems only" chart or alert without hand-picking names from the
 * source, and a rise in self-heal traffic read as a rise in real problems.
 *
 * This module holds everything that differs between the tiers, in one place, with no
 * Angular and no Sentry import — so it stays unit-testable (same reason
 * `error-patterns.ts` sits beside `logging.service.ts` rather than inside it).
 */

/**
 * Which severity tier a report belongs to.
 *
 * The string VALUES are deliberately lowercase rather than the project's usual PascalCase:
 * they leave the app as a Sentry tag value and as a Mixpanel event property value, where
 * humans read them off charts and type them into saved searches.
 */
export enum ProblemKind {
  /** A self-heal already ran, or an expected rare condition was observed. Nothing is
   * wrong, and nobody needs to look at it. */
  Info = 'info',
  /** Still broken, silently. We deliberately do not interrupt the user for it, but a
   * person may eventually need to act on it. */
  Problem = 'problem',
  /** A real caught exception or a user-visible failure. */
  Error = 'error',
}

/** Name of the Sentry tag that carries the tier. Also the Mixpanel property name is
 * `kind` — they differ because `kind` alone would be far too vague as a Sentry tag. */
export const PROBLEM_KIND_TAG = 'problem_kind';

/**
 * Extra Sentry tags a single silent report may carry beside the tier (#4537).
 *
 * Why a report needs tags at all, when `trackSilentProblem` already takes a `context`
 * object. That object reaches Sentry as a BREADCRUMB and nothing else — it is not a
 * column, it cannot be grouped by, and `get_issue_tag_values` cannot see it. So the
 * question "which page does PageWillEnterTimeout name, and in what proportion?" could
 * only be answered by fetching that issue's events one at a time and reading each one's
 * breadcrumbs. A tag is a column Sentry groups by, so the same question takes one click.
 * The reading that made the case is in design-docs/boot-sequence.md, "What the #4413
 * report says, three weeks on".
 *
 * Values are strings because that is what a Sentry tag is; a number would be coerced
 * silently and read back differently.
 *
 * KEEP THE VALUES LOW-CARDINALITY. A tag is an index: page names, step names and status
 * words belong here; a document id, a user id, a timestamp or an error message does not.
 * A high-cardinality tag makes the issue's tag breakdown useless and costs storage for
 * an answer nobody can read.
 *
 * The tier tag is NOT settable through this. `LoggingService.captureMessage` spreads
 * these first and applies `PROBLEM_KIND_TAG` last, so a caller cannot overwrite it —
 * that tag is what makes `problem_kind` a complete partition of every Sentry event, and
 * a report that silently dropped out of it would vanish from every "real problems only"
 * search with nothing saying so.
 */
export type SentryReportTags = Record<string, string>;

/**
 * Whether a silent report also sends its `sentry_error` row to Mixpanel.
 *
 * Every silent report does by default. `Skip` exists for a report whose only use is its
 * rate, which is read in Sentry, and whose Mixpanel rows nobody reads — the identity-map
 * canary in `DatabaseWrapper` is the case that made it (df-e7ce2d515270). Sentry and
 * Mixpanel pass the same rate limiter, so skipping the Mixpanel row loses no count.
 */
export enum MixpanelCopy {
  Send = 'send',
  Skip = 'skip',
}

/** Everything that differs between the tiers when a silent report is written. */
export interface SilentReportShape {
  /** The Sentry message. The prefix is part of Sentry's grouping key, so changing a
   * report's tier deliberately opens a NEW Sentry issue and lets the old one go quiet. */
  sentryMessage: string;
  /** Assignable to Sentry's `SeverityLevel` without importing Sentry here. */
  sentryLevel: 'info' | 'error';
  /** Which `console` function to write the local line with. The informational tier uses
   * `log` on purpose: it is a record of a COMPLETED repair, not an error, so it must not
   * reach the E2E unexpected-browser-error gate (which only inspects SEVERE/ERROR). The
   * same reasoning is already applied in `DatabaseRecoveryService.interceptDatabaseError`. */
  consoleLevel: 'log' | 'error';
  /** Whether `LoggingService` fires the Mixpanel `sentry_error` row for this report. The
   * Sentry message, breadcrumb and console line are written either way. */
  sendsMixpanelRow: boolean;
}

/**
 * Work out how a silent report of the given tier should be written.
 *
 * Only the two SILENT tiers are accepted. `ProblemKind.Error` has no shape here — a real
 * error reports through `trackAbnormalException` / `trackAbnormalError`, which capture the
 * error itself rather than a named message. Taking the whole enum would let
 * `describeSilentReport(ProblemKind.Error, …)` compile and quietly return a `PROBLEM: …`
 * shape, mislabelling a real error as the middle tier.
 */
export function describeSilentReport(
  kind: ProblemKind.Info | ProblemKind.Problem,
  name: string,
  mixpanelCopy: MixpanelCopy = MixpanelCopy.Send,
): SilentReportShape {
  const sendsMixpanelRow = mixpanelCopy !== MixpanelCopy.Skip;
  // if this is the informational tier, report it as information rather than as a fault
  if (kind === ProblemKind.Info) {
    return { sentryMessage: `INFO: ${name}`, sentryLevel: 'info', consoleLevel: 'log', sendsMixpanelRow };
  }
  // otherwise keep the pre-#4382 shape exactly, so existing Sentry issues stay intact
  return { sentryMessage: `PROBLEM: ${name}`, sentryLevel: 'error', consoleLevel: 'error', sendsMixpanelRow };
}

/**
 * The leading text of the console line a silent report writes.
 *
 * Kept as its own function ONLY so a test can pin the exact wording. It is
 * load-bearing text: several E2E specs allow-list expected console errors by
 * matching `Silent problem (Sentry: …` and `…problem: <Name>)`, so rewording it
 * would silently stop every one of those allow-lists matching, and the specs
 * would start failing on errors they were written to tolerate. The problem tier's
 * wording is exactly what it was before #4382 for the same reason.
 *
 * The report's CONTEXT is not part of this. `LoggingService` passes it as a
 * separate argument, as text — see `describeContextForLogLine`.
 */
export function silentReportConsoleLine(
  kind: ProblemKind.Info | ProblemKind.Problem,
  sentryId: string,
  reportName: string,
): string {
  return `Silent ${kind} (Sentry: ${sentryId}, ${kind}: ${reportName})`;
}

/**
 * Make sure a Sentry event carries a tier tag, defaulting it to `error`.
 *
 * This is what makes `problem_kind` a COMPLETE partition of every Sentry event rather
 * than a label on a subset. Plenty of events never pass through `LoggingService` at all —
 * the Angular `ErrorHandler`, unhandled promise rejections, anything Sentry captures on
 * its own. Those are real errors. Without this default they would carry no tag, and a
 * saved search written as "real problems only" would silently drop exactly the events
 * that matter most.
 *
 * Mutates and returns the same object, matching how `beforeSend` already handles tags.
 */
export function applyDefaultProblemKindTag<T extends Record<string, unknown>>(tags: T): T {
  // if one of the three tiers already declared the tier, leave its answer alone
  if (tags[PROBLEM_KIND_TAG] === undefined) {
    (tags as Record<string, unknown>)[PROBLEM_KIND_TAG] = ProblemKind.Error;
  }
  return tags;
}
